/**
 * Which skin the signed-in app wears (More → Appearance).
 *
 * - `eleven-light` / `eleven-warm`: the ElevenLabs-style skin
 *   (design-mockups/eleven-tasks.html, directions E1 and E3), `ui/eleven/eleven.css`.
 * - `jrpg`: the B5 field skin, `ui/jrpg/jrpg.css`, with its own window colour.
 *
 * One device-local setting, written to `<html data-skin>` (and `data-eleven-tone`
 * for the two Eleven directions) before the first paint by `main.tsx`, so the
 * sign-in screen wears it too. The shell keeps it current after that.
 */

export const SKINS = [
  { key: "eleven-light", label: "Eleven Light", note: "White, hairlines, black buttons" },
  { key: "eleven-warm", label: "Eleven Warm", note: "Cream paper, orange accent" },
  { key: "jrpg", label: "JRPG Field", note: "Pixel field, framed windows" },
] as const;

export type Skin = (typeof SKINS)[number]["key"];

export const DEFAULT_SKIN: Skin = "eleven-light";
export const SKIN_STORAGE_KEY = "busee.skin";
export const SKIN_EVENT = "busee:skin-change";

export function isSkin(value: unknown): value is Skin {
  return SKINS.some((skin) => skin.key === value);
}

/** The stored skin, or the default for a missing, stale or unreadable value. */
export function readSkin(storage: Pick<Storage, "getItem"> | null): Skin {
  try {
    const value = storage?.getItem(SKIN_STORAGE_KEY);
    return isSkin(value) ? value : DEFAULT_SKIN;
  } catch {
    return DEFAULT_SKIN;
  }
}

export function applySkin(skin: Skin, root: HTMLElement = document.documentElement): void {
  if (skin === "jrpg") {
    root.dataset.skin = "jrpg";
    delete root.dataset.elevenTone;
  } else {
    root.dataset.skin = "eleven";
    root.dataset.elevenTone = skin === "eleven-warm" ? "warm" : "light";
  }
}

export function clearSkin(root: HTMLElement = document.documentElement): void {
  delete root.dataset.skin;
  delete root.dataset.elevenTone;
}

export function deviceStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Saves the skin and tells the shell; `false` when this device would not store it. */
export function saveSkin(skin: Skin): boolean {
  let stored = true;
  try {
    window.localStorage.setItem(SKIN_STORAGE_KEY, skin);
  } catch {
    stored = false;
  }
  window.dispatchEvent(new CustomEvent<Skin>(SKIN_EVENT, { detail: skin }));
  return stored;
}
